import type { CampaignSummary } from "@decibeltrade/sdk";

export interface OptimisticClaim {
  campaignId: number;
  txHash: string;
  nowSec: number;
}

export function applyOptimisticClaimBatch(
  prev: CampaignSummary,
  metas: readonly OptimisticClaim[],
): CampaignSummary {
  return metas.reduce(applyOptimisticClaim, prev);
}

/** Mirrors the server derivation in `rust/trading-api/src/http/handle_campaigns.rs` — keep formulas in sync. */
export function applyOptimisticClaim(
  prev: CampaignSummary,
  meta: OptimisticClaim,
): CampaignSummary {
  const row = prev.claims.find((c) => c.campaignId === meta.campaignId);
  if (!row) {
    return prev;
  }

  const delta = row.readyToClaim;
  if (delta === 0) {
    return prev;
  }

  const weeklyBreakdown = bumpCurrentWeekBucket(prev.weeklyBreakdown, delta);
  const totalClaimed = prev.totalClaimed + delta;
  const weeklyWowBps = recomputeWeeklyWowBps(weeklyBreakdown, totalClaimed);

  return {
    ...prev,
    readyToClaim: prev.readyToClaim - delta,
    totalClaimed,
    yearToDate: prev.yearToDate + delta,
    weeklyBreakdown,
    weeklyWowBps,
    breakdownByType: prev.breakdownByType.map((b) =>
      b.campaignType === row.campaignType
        ? {
            ...b,
            readyToClaim: b.readyToClaim - delta,
            totalClaimed: b.totalClaimed + delta,
          }
        : b,
    ),
    claims: prev.claims.map((c) =>
      c.campaignId === meta.campaignId
        ? {
            ...c,
            claimedAmount: c.claimedAmount + delta,
            readyToClaim: 0,
            claimedAtTsSec: meta.nowSec,
            claimTxHash: meta.txHash,
          }
        : c,
    ),
  };
}

function bumpCurrentWeekBucket(
  weekly: CampaignSummary["weeklyBreakdown"],
  delta: number,
): CampaignSummary["weeklyBreakdown"] {
  if (weekly.length === 0) {
    return weekly;
  }
  return weekly.map((w, i) =>
    i === weekly.length - 1 ? { ...w, rewardAmount: w.rewardAmount + delta } : w,
  );
}

function recomputeWeeklyWowBps(
  weekly: CampaignSummary["weeklyBreakdown"],
  totalClaimed: number,
): number {
  // Mirrors handle_campaigns.rs:495-497 → wow_basis_points(totalClaimed, prior).
  const thisWeek = weekly[weekly.length - 1]?.rewardAmount ?? 0;
  const prior = Math.max(0, totalClaimed - thisWeek);
  if (prior === 0 || totalClaimed <= prior) {
    return 0;
  }
  return Math.trunc(((totalClaimed - prior) * 10_000) / prior);
}
